/* eslint-disable no-magic-numbers */
import { node } from "~/util/dom";
import RibbonItemNews from "./item-news";
import RibbonItemPhoto from "./item-photo";
import RibbonItemReview from "./item-review";
import RibbonItemPOI from "./item-poi";
import { getStyleValues, calculateWidths } from "./utils";
import { IRibbonData, IRibbonItemNewsData, IRibbonItemPOIData, TOnClickTypes, TRibbonSections } from "./interfaces";
import { ANIMATIONS, ITEM_GAP, MAX_PHOTOS_PER_LINE, MIN_VISIBLE_PX, PHOTOS_MAX_WIDTH, PHOTOS_MULTI_LINE_HEIGHT } from "./const";
import { IContentNumbers, IRibbonEventItemShow } from "./analytics";
import { IPoi } from "~/interfaces";

interface ISectionOutputItem {
	id: number;
	left: number;
	right: number;
	width: number;
	section: TRibbonSections;
	items: RibbonItemNews[] | RibbonItemPhoto[] | RibbonItemReview[] | RibbonItemPOI[];
}

interface ISectionDataItem {
	width: number;
	items: RibbonItemNews[] | RibbonItemPhoto[] | RibbonItemReview[] | RibbonItemPOI[];
}

interface ISectionItem {
	items: Array<ISectionDataItem>;
	name: TRibbonSections;
}

interface IComputedSection {
	items: Array<ISectionOutputItem>;
	addOverItem: boolean;
}

interface IRibbonCont {
	itemGap?: number;
	photosMaxWidth?: number;
	photosLineHeight?: number;
	minVisiblePx?: number;
	data?: IRibbonData;
}

export default class RibbonCont {
	_options: IRibbonCont;

	_isComplete: boolean;

	_dom: {
		container: HTMLDivElement;
		news: HTMLDivElement;
		photos: HTMLDivElement;
		photosLine1: HTMLDivElement;
		photosLine2: HTMLDivElement;
		reviews: HTMLDivElement;
		pois: HTMLDivElement;
	};

	_contents: {
		news: Array<RibbonItemNews>;
		photos: Array<RibbonItemPhoto>;
		reviews: Array<RibbonItemReview>;
		pois: Array<RibbonItemPOI>;
	};

	_onClick: (data: TOnClickTypes) => void;

	_sections: Array<IComputedSection>;

	_showedIds: Array<number>;

	constructor({
		itemGap = ITEM_GAP,
		photosMaxWidth = PHOTOS_MAX_WIDTH,
		photosLineHeight = PHOTOS_MULTI_LINE_HEIGHT,
		minVisiblePx = MIN_VISIBLE_PX,
		data = null,
	}: IRibbonCont) {
		this._options = {
			itemGap,
			photosMaxWidth,
			photosLineHeight,
			minVisiblePx,
		};
		this._contents = {
			news: [],
			photos: [],
			reviews: [],
			pois: [],
		};
		this._isComplete = false;
		this._onClick = () => {};
		this._sections = [];
		this._showedIds = [];

		this._build();

		// zrovna vytvorime
		if (data) {
			this.addNewsItems(data.news);
			this.addPhotosItems(data.photos);
			this.addReviewsItems(data.reviews);
			this.addReviewsPOIs(data.pois);
		}
	}

	get container() {
		return this._dom.container;
	}

	set left(value: number) {
		this._dom.container.style.left = `${value}px`;
	}

	getVisibleItems(step = 0, pageType: IRibbonEventItemShow["pageType"], detailData?: IPoi) {
		const output: Array<IRibbonEventItemShow> = [];

		if (this._sections.length && this._sections[step]) {
			const items: Array<ISectionOutputItem> = this._sections[step].items.slice();

			// vezmeme druhy item z dalsi sekce, ktery nejde videt cely
			if (this._sections[step + 1] && this._sections[step + 1].addOverItem) {
				items.push(this._sections[step + 1].items[0]);
			}

			items.forEach(item => {
				if (this._showedIds.includes(item.id)) {
					return;
				}

				this._showedIds.push(item.id);
				item.items.forEach(oneItem => {
					let contentType: IRibbonEventItemShow["contentType"] = "article";
					let source = "";
					let id = 0;
					let typeId = 0;
					let name = "";
					let url = "";

					if (detailData) {
						source = detailData.source;
						id = detailData.id as number;
						typeId = detailData.typeId;
					}

					switch (item.section) {
						case "news": {
							const curData = oneItem.data as IRibbonItemNewsData;

							contentType = "article";
							name = curData.desc;
							url = curData.linkUrl;
							break;
						}

						case "photos":
							contentType = "photo";
							break;

						case "pois": {
							const curData = oneItem.data as IRibbonItemPOIData;

							contentType = "poi";
							name = curData.poiTitle;
							source = curData.poiSource;
							id = curData.poiId;
							typeId = curData.poiTypeId;
							break;
						}

						case "reviews":
							contentType = "review";
							break;

						default:
					}

					output.push({
						section: "ribbon",
						action: "display_item",
						module: "content",
						contentType,
						content: {
							source,
							id,
							typeId,
							name,
							url,
						},
						pageType,
						...detailData
							? {
								source: detailData.source,
								id: detailData.id as number,
								typeId: detailData.typeId,
							}
							: {},
					});
				});
			});
		}

		return output;
	}

	get loadVisibleItems() {
		const visibleItems: IContentNumbers = {
			articles: 0,
			photos: 0,
			poi: 0,
			reviews: 0,
		};

		if (this._sections.length) {
			const items: Array<ISectionOutputItem> = this._sections[0].items.slice();

			// vezmeme druhy item z dalsi sekce, ktery nejde videt cely
			if (this._sections.length > 1 && this._sections[1].addOverItem) {
				items.push(this._sections[1].items[0]);
			}

			for (const item of items) {
				switch (item.section) {
					case "news":
						visibleItems.articles += item.items.length;
						break;

					case "photos":
						visibleItems.photos += item.items.length;
						break;

					case "reviews":
						visibleItems.reviews += item.items.length;
						break;

					case "pois":
						visibleItems.poi += item.items.length;
						break;

					default:
				}
			}
		}

		return visibleItems;
	}

	onClick(onClick: (data: TOnClickTypes) => void) {
		this._onClick = onClick;
	}

	animShow() {
		return new Promise(resolve => {
			this._dom.container.classList.add("anim-show-start");

			setTimeout(() => {
				this._dom.container.classList.remove("anim-show-start");
				this._dom.container.classList.add("anim-show");
			}, ANIMATIONS.SHOW_START);
			setTimeout(() => {
				this._dom.container.classList.remove("anim-show");
				resolve({});
			}, ANIMATIONS.SHOW_END);
		});
	}

	destroy() {
		return new Promise(resolve => {
			this._dom.container.classList.add("anim-hide");

			setTimeout(() => {
				this._dom.container.remove();
				resolve({});
			}, ANIMATIONS.HIDE);
		});
	}

	getSteps({
		width,
		left,
		right,
	}: {
		width: number;
		left: number;
		right: number,
	}): Array<number> {
		const allSections = this._getAllSections();
		let rightDiff = 0;
		let currentSection: IComputedSection = null;
		const contWidth = width - right;
		const computedSections: Array<IComputedSection> = [];

		for (const section of allSections) {
			const sectionRight = section.right - rightDiff;

			if (sectionRight < contWidth) {
				if (!currentSection) {
					currentSection = {
						items: [],
						addOverItem: false,
					};
				}

				currentSection.items.push(section);
			} else {
				computedSections.push(currentSection);
				rightDiff = section.left;
				currentSection = {
					items: [section],
					addOverItem: false,
				};

				// jde videt cast druhe polozky?
				const visPxs = width - section.left;

				currentSection.addOverItem = visPxs > this._options.minVisiblePx;
			}
		}

		computedSections.push(currentSection);

		const steps: Array<number> = [];
		const lastSection = computedSections.length;

		computedSections.forEach((section, ind) => {
			if (ind === 0) {
				steps.push(0);
			} else if (ind === lastSection - 1) {
				const lastItem = section.items[section.items.length - 1];
				const lastX = lastItem.right - width + right;

				steps.push(-lastX);
			} else {
				const firstItem = section.items[0];

				steps.push(-firstItem.left + left);
			}
		});

		// ulozime
		this._sections = computedSections;

		return steps;
	}

	addNewsItems(items: IRibbonData["news"]) {
		items.forEach(data => {
			const item = new RibbonItemNews({
				...data,
				onClick: () => {
					this._onClick({
						name: "news",
						data,
					});
				},
			});

			this._contents.news.push(item);
			this._dom.news.append(item.container);
		});
	}

	addPhotosItems(items: IRibbonData["photos"]) {
		this._dom.photos.append(this._dom.photosLine1);

		const count = items.length;
		const twoLines = count > MAX_PHOTOS_PER_LINE;
		const dataWidths = calculateWidths({
			items,
			perLine: MAX_PHOTOS_PER_LINE,
			gap: this._options.itemGap,
		});

		if (twoLines) {
			this._dom.photos.append(this._dom.photosLine2);
			this._dom.photos.classList.add("two-lines");
			this._dom.photos.style.width = `${this._options.photosMaxWidth}px`;
			this._dom.photosLine1.style.width = `${this._options.photosMaxWidth}px`;
			this._dom.photosLine1.style.height = `${this._options.photosLineHeight}px`;
			this._dom.photosLine2.style.width = `${this._options.photosMaxWidth}px`;
			this._dom.photosLine2.style.height = `${this._options.photosLineHeight}px`;
		} else if (count < MAX_PHOTOS_PER_LINE) {
			const calcWidth = (this._options.photosMaxWidth / MAX_PHOTOS_PER_LINE) * count >>> 0;

			this._dom.photosLine1.style.width = `${calcWidth}px`;
		}

		items.forEach((data, ind) => {
			const item = new RibbonItemPhoto({
				...data,
				onClick: () => {
					this._onClick({
						name: "photos",
						data,
					});
				},
			});
			const curWidth = dataWidths[ind].width;

			if (ind < MAX_PHOTOS_PER_LINE) {
				this._dom.photosLine1.append(item.container);
			} else {
				this._dom.photosLine2.append(item.container);
			}

			item.setMatrixType("two-lines-dyn-width", curWidth, dataWidths[ind].gap);
			this._contents.photos.push(item);
		});
	}

	addReviewsItems(items: IRibbonData["reviews"]) {
		items.forEach(data => {
			const item = new RibbonItemReview({
				...data,
				onClick: () => {
					this._onClick({
						name: "reviews",
						data,
					});
				},
			});

			this._contents.reviews.push(item);
			this._dom.reviews.append(item.container);
		});
	}

	addReviewsPOIs(items: IRibbonData["pois"]) {
		items.forEach(data => {
			const item = new RibbonItemPOI({
				...data,
				onClick: () => {
					this._onClick({
						name: "pois",
						data,
					});
				},
			});

			this._contents.pois.push(item);
			this._dom.pois.append(item.container);
		});
	}

	_build() {
		const container = node("div", { className: "ribbonCont" });

		this._dom = {
			container,
			news: node("div", { className: "ribbonCont__news" }),
			photos: node("div", { className: "ribbonCont__photos" }),
			photosLine1: node("div", { className: "ribbonCont__photosLine1" }),
			photosLine2: node("div", { className: "ribbonCont__photosLine2" }),
			reviews: node("div", { className: "ribbonCont__reviews" }),
			pois: node("div", { className: "ribbonCont__pois" }),
		};
		this._dom.container.append(this._dom.reviews, this._dom.photos, this._dom.pois, this._dom.news);
		this._dom.photos.style.maxWidth = `${this._options.photosMaxWidth}px`;
		this._dom.photosLine1.style.maxWidth = `${this._options.photosMaxWidth}px`;
		this._dom.photosLine2.style.maxWidth = `${this._options.photosMaxWidth}px`;
	}

	_getSection(section: TRibbonSections) {
		switch (section) {
			case "news":
				return this._contents.news;

			case "photos":
				return this._contents.photos;

			case "reviews":
				return this._contents.reviews;

			case "pois":
				return this._contents.pois;

			default:
		}

		return null;
	}

	_getSectionData(section: TRibbonSections) {
		const items = this._getSection(section);
		const output: Array<ISectionDataItem> = [];

		if (section === "photos") {
			output.push({
				width: this._dom.photos.offsetWidth || this._options.photosMaxWidth,
				items,
			});
		} else {
			items.forEach(item => {
				output.push({
					width: item.width,
					items: [item],
				});
			});
		}

		return output;
	}

	_getAllSectionData() {
		const news = this._getSectionData("news");
		const photos = this._getSectionData("photos");
		const reviews = this._getSectionData("reviews");
		const pois = this._getSectionData("pois");
		const sections = ([{
			items: reviews,
			name: "reviews",
		}, {
			items: photos,
			name: "photos",
		}, {
			items: pois,
			name: "pois",
		}, {
			items: news,
			name: "news",
		}] as Array<ISectionItem>).filter(sectionItem => sectionItem.items.length > 0);

		return sections;
	}

	_getAllSections() {
		const sections = this._getAllSectionData();
		const itemGap = this._options.itemGap;
		const [contentGap] = getStyleValues(this._dom.container, ["gap"]);
		const sectionsLen = sections.length;
		const output: Array<ISectionOutputItem> = [];
		let left = 0;
		let itemId = 0;

		sections.forEach((sectionItem, ind) => {
			const secLen = sectionItem.items.length;

			sectionItem.items.forEach((item, secInd) => {
				const itemLen = left;
				const itemRight = left + item.width;

				output.push({
					id: itemId,
					left: itemLen,
					right: itemRight,
					width: item.width,
					section: sectionItem.name,
					items: item.items,
				});
				itemId++;
				left = itemRight + (secInd === secLen - 1 ? 0 : itemGap);
			});

			left += ind === sectionsLen - 1 ? 0 : contentGap;
		});

		return output;
	}
}
